import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  getProcurementDamageReportData,
  type ProcurementDamageReportData,
  type DamageBySupplier,
  type DamageByProduct,
  type DamageSummary
} from '@/services/procurementDamageReportService';

export interface QualityIntelligenceFilters {
  supplierName?: string;
  productName?: string;
  entityType?: string; // 'farm' | 'supplier' | 'agent' | 'all'
  entityName?: string;
}

export interface QualityIntelligenceData {
  summary: DamageSummary;
  supplierAggregation: DamageBySupplier[];
  productAggregation: DamageByProduct[];
  allSupplierNames: string[];
  allProductNames: string[];
  allEntityNames: string[]; // Dynamic based on entity type
}

export function useQualityIntelligence(
  startDate: Date,
  endDate: Date,
  filters?: QualityIntelligenceFilters,
  draftEntityType?: string // For populating entity name dropdown
) {
  // Fetch raw damage report data
  const { data: rawData, isLoading, isError, error } = useQuery<ProcurementDamageReportData>({
    queryKey: ['quality-intelligence', startDate.toISOString(), endDate.toISOString()],
    queryFn: () => getProcurementDamageReportData(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  // Apply client-side filtering and recalculate summary
  const filteredData = useMemo<QualityIntelligenceData | null>(() => {
    if (!rawData) return null;

    // Extract all unique names for dropdowns FROM FULL DATASET (not filtered)
    const allSupplierNames = Array.from(
      new Set(rawData.bySupplier.map(s => s.supplier_name))
    ).sort();
    
    const allProductNames = Array.from(
      new Set(rawData.byProduct.map(p => p.product_name))
    ).sort();

    // Extract entity names based on DRAFT entity type (for dropdown population)
    // This allows users to see available entities before clicking Apply
    const entityTypeForDropdown = draftEntityType || 'all';
    let allEntityNames: string[] = [];
    
    if (entityTypeForDropdown !== 'all') {
      allEntityNames = Array.from(
        new Set(
          rawData.bySupplier
            .filter(s => s.source_type === entityTypeForDropdown)
            .map(s => s.supplier_name)
        )
      ).sort();
    } else {
      allEntityNames = allSupplierNames;
    }

    // Apply filters to data (using filterApplied, not draft)
    let filteredSuppliers = rawData.bySupplier;
    let filteredProducts = rawData.byProduct;
    let filteredRecords = rawData.records;

    // Entity type filter
    if (filters?.entityType && filters.entityType !== 'all') {
      filteredSuppliers = filteredSuppliers.filter(
        s => s.source_type === filters.entityType
      );
      filteredRecords = filteredRecords.filter(
        r => r.source_type === filters.entityType
      );
    }

    // Entity name filter
    if (filters?.entityName) {
      filteredSuppliers = filteredSuppliers.filter(
        s => s.supplier_name === filters.entityName
      );
      filteredRecords = filteredRecords.filter(
        r => r.source_name === filters.entityName
      );
    }

    // Product name filter
    if (filters?.productName) {
      filteredProducts = filteredProducts.filter(
        p => p.product_name === filters.productName
      );
      filteredRecords = filteredRecords.filter(
        r => r.product_name === filters.productName
      );
    }

    // Recalculate summary based on filtered records
    const summary: DamageSummary = filteredRecords.reduce(
      (acc, record) => ({
        total_net_damage_kg: acc.total_net_damage_kg + record.net_damage_kg,
        total_compensation_kg: acc.total_compensation_kg + record.compensation_kg,
        total_rejected_items: acc.total_rejected_items + (record.is_rejected ? 1 : 0),
        total_damage_value: acc.total_damage_value + (record.net_damage_kg * record.price_per_unit),
        total_ordered_kg: acc.total_ordered_kg + record.ordered_quantity,
        damage_rate: 0,
      }),
      {
        total_net_damage_kg: 0,
        total_compensation_kg: 0,
        total_rejected_items: 0,
        total_damage_value: 0,
        total_ordered_kg: 0,
        damage_rate: 0,
      }
    );

    summary.damage_rate = summary.total_ordered_kg > 0
      ? (summary.total_net_damage_kg / summary.total_ordered_kg) * 100
      : 0;

    return {
      summary,
      supplierAggregation: filteredSuppliers,
      productAggregation: filteredProducts,
      allSupplierNames,
      allProductNames,
      allEntityNames,
    };
  }, [rawData, filters, draftEntityType]);

  return {
    data: filteredData,
    isLoading,
    isError,
    error,
  };
}
